import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { bia, statusCommand } from '../../../core/services';
import { Box, Button, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Typography } from '@material-ui/core';
import { Loader, SGSButton, SGSCard, SGSDatePicker, SGSHelpButton, SGSTable, SGSTextButton, SGSTooltip } from '../../../components';
import SGSDrawer from '../../../components/SGSDrawer';
import { enumStatusCommand } from '../../../core/services/statusCommand';
import { mercure } from '../../../core/utils';
import moment from 'moment';
import { BIA } from '../../../core/models';
import { Cell, ColumnInterface, Row } from 'react-table';
import { CacheData, initBIA } from 'core/models/BIA';
import styles from './BiaEnLigne.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { exportBias, souscripteursContratBiaEnLigne } from 'core/services/bia';
import { addMonths, format, isValid } from 'date-fns';
import SGSFormAutoComplete from 'components/Form/AutoComplete';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import classNames from 'classnames';
import { GridCloseIcon } from '@mui/x-data-grid';
import _, { delay } from 'lodash';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Link } from 'react-router-dom';
import PQueue from 'p-queue/dist';
import * as yup from 'yup';
import { List, ListItem, ListItemText } from '@material-ui/core';
import SGSModalLight from 'components/SGSModalLight';

import modalLightStyles from '../../../components/SGSModalLight/SGSModalLight.module.scss';
import errorIcon from './../../../assets/error_icon.svg';
import { useCustomStyles } from 'core/mui/customStyles';
import { isVisitor } from 'core/utils/roles';
import { useAppContext } from 'core/contexts';

type Props = {};

// Possible states for BIA validation
enum BiaValidationState {
    Initial = 'INITIAL',
    Processing = 'PROCESSING',
    Success = 'SUCCESS',
    Error = 'ERROR',
}

enum BiaStatut {
    TO_BE_VALIDATED = 'toBeValidated',
    ON_GOING = 'onGoing',
    VALIDATED = 'validated',
    ERROR = 'error',
}

const queue = new PQueue({ concurrency: 1 });

const BiaEnLigne: React.FC<Props> = () => {
    const { user } = useAppContext();
    const { t } = useTranslation();
    const commonStyles = useCustomStyles();
    const customStyles = useCustomStyles();
    const [contrats, _setContrats] = useState<BIA[]>([]);
    const [loading, setLoading] = useState(false);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedContract, setSelectedContract] = useState<BIA>(initBIA);
    const [ongoingValidates, setOngoingValidates] = useState<any>([]);
    const [biaSubmitted, setBiaSubmitted] = useState<any>([]); // store BIA submitted data by ID
    const [eventList, _setEventList] = useState([]);
    const [selectedDateEmbauche, setSelectedDateEmbauche] = useState(null);
    const [selectedDateEffet, setSelectedDateEffet] = useState(null);
    const [minDateEffet, setMinDateEffet] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [listContrat, setListContrat] = useState([]);
    const [loading2, setLoading2] = useState(false);
    const [optionSelected, setOptionSelected] = useState(null);
    const [isBiaValidated, setIsBiaValidated] = useState(false);
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [biaDiseabled, setBiaDiseabled] = React.useState<readonly string[]>([]);
    const [biaValidated, setBiaValidated] = React.useState<readonly string[]>([]);
    const [biaInError, setBiaInError] = React.useState<readonly string[]>([]);
    const [displaySelected, setDisplaySelected] = useState(false);
    const [open, setOpen] = useState(false);
    const [allBiaValidationState, setAllBiaValidationState] = useState(BiaValidationState.Initial);
    const [errorsBiaValidation, setErrorsBiaValidation] = useState([]);
    const [biaToSetHasValidated, setBiaToSetHasValidated] = useState([]);
    const [biaToSetHasError, setBiaToSetHasError] = useState([]);
    const [cache, setCache] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errorForBiaSelection, setErrorForBiaSelection] = useState(null);
    const [isSubmitDiseabled, setIsSubmitDiseabled] = useState(null);
    const [hasBiaChanged, setHasBiaChanged] = useState(false);
    const [hasDateEmbaucheChanged, setHasDateEmbaucheChanged] = useState(false);

    const contratsRef = useRef(contrats);
    const setContrats = (data: BIA[]) => {
        contratsRef.current = data;
        _setContrats(data);
    };

    const eventListRef = useRef(eventList);
    const setEventList = (data) => {
        eventListRef.current = data;
        _setEventList(data);
    };

    const onGoing = (id) => ongoingValidates.find((o) => o.data.idAssure === id);
    const onGoingCommand = onGoing(selectedContract.idAssure);

    const getValidatedBia = (biaId) => {
        return ongoingValidates.map((command) => command.data.id).filter((id) => id === biaId);
    };

    const getBiaStatusIcon = useCallback(
        (row): string => {
            if (biaInError.includes(row.id)) return BiaStatut.ERROR;
            if (biaValidated.includes(row.id)) return BiaStatut.VALIDATED;
            if (getValidatedBia(row.id).length) return BiaStatut.ON_GOING;
            return BiaStatut.TO_BE_VALIDATED;
        },
        [biaInError, biaValidated, ongoingValidates],
    );

    const columns = useMemo<ColumnInterface<BIA>[]>(
        () => [
            {
                Header: t('affiliations.contrat.bia.name'),
                accessor: (row: BIA) => `${row.numSecu} ${row.nom} ${row.prenom}`,
                maxWidth: 200,
                Cell: ({ row: { original: row } }: Cell<BIA>) => (
                    <Box>
                        <Box className={styles.grey}>{row.numSecu}</Box>
                        <Box className={styles.strong}>
                            {row.nom} {row.prenom}
                        </Box>
                    </Box>
                ),
            },
            {
                Header: t('affiliations.contrat.bia.contract'),
                accessor: 'tarifCoti',
                maxWidth: 100,
            },
            {
                Header: t('affiliations.contrat.bia.company'),
                accessor: (row: BIA) => `${row.siret} ${row.libelle}`,
                maxWidth: 200,
                Cell: ({ row: { original: row } }: Cell<BIA>) => (
                    <Box>
                        <Box className={styles.grey}>{row.siret}</Box>
                        <Box className={styles.strong}>{row.libelle}</Box>
                    </Box>
                ),
            },
            {
                Header: t('affiliations.contrat.bia.options'),
                accessor: 'info',
                maxWidth: 200,

                Cell: ({ value }: Cell<BIA>) => <Box>{value ? <Box>{value}</Box> : <Box>{t('affiliations.contrat.bia.noComment')}</Box>}</Box>,
            },
            {
                Header: t('affiliations.contrat.bia.dateEmbauche'),
                accessor: (row: BIA) => `${row.dateEmbauche ? moment(row.dateEmbauche, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}`,
                maxWidth: 100,
                Cell: ({ row: { original: row } }: Cell<BIA>) => <Box>{row.dateEmbauche ? moment(row.dateEmbauche, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}</Box>,
            },
            {
                Header: t('affiliations.contrat.bia.dateEffet'),
                accessor: (row: BIA) => `${row.dateEmbauche ? moment(row.dateEmbauche, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}`,
                maxWidth: 90,
                Cell: ({ row: { original: row } }: Cell<BIA>) => <Box>{row.dateEffet ? moment(row.dateEffet, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}</Box>,
            },
            {
                Header: '',
                accessor: 'none',
                className: 'p-0 align-items-stretch',
                minWidth: 45,
                maxWidth: 45,
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row: { original: row } }: Cell<BIA>) => {
                    return (
                        <div className={styles.tableActions}>
                            {getBiaStatusIcon(row) === BiaStatut.TO_BE_VALIDATED ? (
                                <div className={styles.tableActions__chrono}>
                                    <Box className={classNames(customStyles.txtBlueDarkBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                        <Box className={customStyles.icon}>
                                            <i className="fas fa-stopwatch"></i>
                                        </Box>
                                    </Box>
                                </div>
                            ) : (
                                ''
                            )}

                            {getBiaStatusIcon(row) === BiaStatut.ON_GOING ? (
                                <div className={styles.tableActions__chrono}>
                                    <Box className={classNames(customStyles.txtGrayLightBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                        <Box className={customStyles.icon}>
                                            <i className="fas fa-hourglass"></i>
                                        </Box>
                                    </Box>
                                </div>
                            ) : (
                                ''
                            )}

                            {getBiaStatusIcon(row) === BiaStatut.VALIDATED ? (
                                <div className={styles.tableActions__chrono}>
                                    <Box className={classNames(customStyles.txtGreenDarkBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                        <Box className={customStyles.icon}>
                                            <i className="fas fa-check-circle"></i>
                                        </Box>
                                    </Box>
                                </div>
                            ) : (
                                ''
                            )}

                            {getBiaStatusIcon(row) === BiaStatut.ERROR ? (
                                <div className={styles.tableActions__chrono}>
                                    <Box className={classNames(customStyles.txtRedDarkBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                        <Box className={customStyles.icon}>
                                            <i className="fas fa-exclamation-circle"></i>
                                        </Box>
                                    </Box>
                                </div>
                            ) : (
                                ''
                            )}

                            <SGSTooltip title={<Box>{t('details')}</Box>}>
                                <div className={styles.tableActions__menu} onClick={() => {}}>
                                    <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                                </div>
                            </SGSTooltip>
                        </div>
                    );
                },
            },
        ],
        [t, biaDiseabled, biaValidated, biaInError, biaDiseabled, isSubmitDiseabled, onGoingCommand],
    );

    const toggleDrawer = (open: boolean) => {
        setMenuOpen(open);
    };

    /**
     * Retrieves and caches option (souscrip list) for a given souscripteur and tarifCoti combination.
     * @param {string} idSouscripteur - The ID of the souscripteur.
     * @param {string} tarifCoti - The tarifCoti value.
     * @returns {Array} An array of options with their corresponding values and labels.
     */
    const cacheOptions = async (idSouscripteur, tarifCoti) => {
        // Set loading state to true while fetching options
        setIsLoadingOptions(true);

        // Generate a unique ID for the cache entry
        const id = idSouscripteur + '_' + tarifCoti;

        // Check if options are already cached
        if (cache[id]) {
            setIsLoadingOptions(false);
            return cache[id];
        }

        const list = await souscripteursContratBiaEnLigne(idSouscripteur, tarifCoti);
        const options = list.map((e) => {
            return { value: e.contrat.contIntern, label: e.souscrip.libelle, idSouscripteur: e.souscrip.cod, datDeb: e.contrat.datDeb, tarifCoti };
        });

        // Update the cache with the fetched options using the generated ID as the key
        setCache((cache) => ({
            ...cache,
            [id]: options, // Store the fetched data in the cache using the id as key
        }));

        setIsLoadingOptions(false);

        return options;
    };

    const onRowClick = async (row: Row<BIA>) => {
        // Reset changed
        setHasBiaChanged(false);
        setHasDateEmbaucheChanged(false);
        setIsBiaValidated(false);
        setLoading2(true);

        const biaData: BIA = { ...row.original }; // Create a shallow copy

        // Get submitted payload, if any
        const submittedPayload = biaSubmitted[biaData.id];
        if (submittedPayload) {
            const { dateEmbauche, contIntern, dateEffet } = submittedPayload;
            biaData.dateEmbauche = dateEmbauche;
            biaData.contIntern = contIntern;
            biaData.dateEffet = dateEffet;
        }

        setValue('dateEmbauche', biaData.dateEmbauche);
        setSelectedDateEmbauche(biaData.dateEmbauche);

        setListContrat([]);

        const formattedDateEffet = biaData.dateEffet ? format(new Date(biaData.dateEffet), 'dd/MM/yyyy') : null;
        setValue('dateEffet', formattedDateEffet);
        setSelectedDateEffet(biaData.dateEffet);

        setSelectedContract({ ...biaData });
        toggleDrawer(true);

        const options = await cacheOptions(biaData.idSouscripteur, biaData.tarifCoti);
        setListContrat(options);
        const selected = options.filter((e) => e.idSouscripteur === row.original.idSouscripteur); // TODO :: add filter on contract date
        setOptionSelected(selected[0]);
        setValue('optionSelected', selected[0]);
        setLoading2(false);
        trigger();

        // Check status if BIA is onGoing
        if (onGoingCommand?.data?.id === row.original.id) {
            const res = await statusCommand.getBiaById(row.original.id);
            if (!res.biaExist) {
                setBiaValidated((biaValidated) => [...biaValidated, onGoingCommand?.data?.id]);
            }
        }
    };

    useEffect(() => {
        biaToSetHasValidated.forEach((id) => {
            const elementValidated = ongoingValidates.find((o) => o.id === id);
            if (elementValidated) setBiaValidated((biaValidated) => [...biaValidated, elementValidated.data.id]);
            setOngoingValidates((ongoingValidates) => ongoingValidates.filter((o) => o.id !== id));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [biaToSetHasValidated]);

    // Add BIA to error list (validation failed)
    useEffect(() => {
        biaToSetHasError.forEach((id) => {
            const elementValidated = ongoingValidates.find((o) => o.id === id);
            if (elementValidated) setBiaInError((biaInError) => [...biaInError, elementValidated.data.id]);
            setOngoingValidates((ongoingValidates) => ongoingValidates.filter((o) => o.id !== id));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [biaToSetHasError]);

    const mercureFunction = (event) => {
        if (event.Status === 'OK') {
            queue.add(() => setBiaToSetHasValidated((biaToSetHasValidated) => [...biaToSetHasValidated, event.id]));
        } else {
            queue.add(() => setBiaToSetHasError((biaToSetHasError) => [...biaToSetHasError, event.id]));
        }

        setIsBiaValidated(false);
    };

    /**
     * Create a Mercure listener for the specified ID.
     * @param {string} id - The ID of the Mercure event stream.
     */
    const createMercureListener = (id) => {
        const eventSource = mercure.createListener(id, mercureFunction);
        setEventList([...eventListRef.current, eventSource]);
    };

    /**
     * Adds a new BIA to the ongoingValidates array with the given mercure id and data (asynchronous)
     * @param {Object} param - An object containing the id and data properties.
     * @param {string|number} param.id - The mercure id to assign to the new object.
     * @param {*} param.data - The data to assign to the new object.
     */
    const addOngoingValidates = ({ id, data }) => {
        setOngoingValidates((ongoingValidates) => [...ongoingValidates, { id, data }]);
    };

    /**
     * Handles validation of a BIA contract while form is valid.
     * @param {string} id - The ID of the contract to validate.
     * @param {boolean} sansSuite - Whether to validate or reject the contract (sansSuite:true => rejected).
     * @returns {Promise<void>} - A promise that resolves when the validation is complete.
     */
    const handleValidate = async (id, sansSuite) => {
        if (isVisitor(user)) return false;
        setIsSubmitDiseabled(true);

        trigger();
        if (Object.keys(errors).length !== 0) return false;

        const dateEmbauche = moment(getValues('dateEmbauche')).format('yyyy-MM-DD');
        const dateEffetFormatted = moment(getValues('dateEffet'), 'DD/MM/yyyy').format('yyyy-MM-DD');
        const newContrat = optionSelected.value !== selectedContract.contIntern ? optionSelected.value : '';

        let biaToValidate: BIA = contrats.find((o) => o.idAssure === id);
        const cacheData = createBiaCacheData(biaToValidate, sansSuite, 'form');

        const payload = {
            idAssure: biaToValidate.idAssure,
            dateEmbauche: hasDateEmbaucheChanged ? dateEmbauche : biaToValidate.dateEmbauche,
            dateEffet: hasDateEmbaucheChanged ? dateEffetFormatted : biaToValidate.dateEffet,
            sansSuite: sansSuite,
            // TODO :: remove hasBiaChanged and use optionSelected when full list of contract is available
            contIntern: biaToValidate.contIntern,
            newContrat: hasBiaChanged ? newContrat : biaToValidate.contIntern,
            nom: biaToValidate.nom,
            prenom: biaToValidate.prenom,
            numSecu: biaToValidate.numSecu,
            CommandCacheData: JSON.stringify(cacheData), // Keep a log of the original BIA
        };

        // Store submitted data
        setBiaSubmitted((biaSubmitted) => ({
            ...biaSubmitted,
            [biaToValidate.id]: { ...payload }, // Store the fetched data in the cache using the id as key
        }));

        bia.validateBia(biaToValidate.idSouscripteur, biaToValidate.tarifCoti, payload)
            .then(async (response: any) => {
                queue.add(() => addOngoingValidates({ id: response.id, data: cacheData }));
                createMercureListener(response.id);
                setIsBiaValidated(true);
                setBiaDiseabled(makeUnique([...biaDiseabled, cacheData.id]));
                toast.success(t('affiliations.contrat.bia.successPost'));
            })
            .catch(() => {
                toast.error(t('affiliations.contrat.bia.errorPost'));
            })
            .finally(() => setIsSubmitDiseabled(false));
    };

    /**
     * Sends batches of BIA posts for validation.
     * @param {Array} selected - The list of BIA IDs to validate.
     * @param {Boolean} sansSuite - Whether to validate or reject the BIA.
     */
    const validateAllBiaPosts = async (selected, sansSuite) => {
        if (isVisitor(user)) return false;
        setAllBiaValidationState(BiaValidationState.Processing);
        const start = new Date();
        const transition = 700;
        const batchSize = 10;
        const batches = _.chunk(selected, batchSize);
        const errors = [];

        let biaDiseabledList = [...biaDiseabled];

        const mercureListenerToAdd = [];

        for (const batch of batches) {
            await Promise.all(
                batch.map(async (id) => {
                    const biaToValidate = contrats.find((o) => o.id === id);
                    const cacheData = createBiaCacheData(biaToValidate, sansSuite, 'lot');

                    // Validate data
                    handleSelectedValidation(id);

                    try {
                        const { idSouscripteur, tarifCoti } = biaToValidate;
                        const { dateEmbauche, dateEffet } = cacheData;
                        const CommandCacheData: string = JSON.stringify(cacheData);

                        const response = await bia.validateBia(idSouscripteur, tarifCoti, {
                            idAssure: biaToValidate.idAssure,
                            dateEmbauche,
                            dateEffet: dateEffet,
                            sansSuite,
                            contIntern: biaToValidate.contIntern,
                            newContrat: biaToValidate.contIntern,
                            nom: biaToValidate.nom,
                            prenom: biaToValidate.prenom,
                            numSecu: biaToValidate.numSecu,
                            CommandCacheData,
                        });
                        queue.add(() => addOngoingValidates({ id: response.id, data: cacheData }));
                        mercureListenerToAdd.push(response.id);
                        biaDiseabledList = [...biaDiseabledList, cacheData.id];
                    } catch (error) {
                        errors.push({ bia: biaToValidate, error: error });
                    }
                }),
            );
        }

        for (let i = 0; i < mercureListenerToAdd.length; i++) {
            createMercureListener(mercureListenerToAdd[i]);
        }

        delayCallback(start, transition, (): void => {
            if (errors.length) {
                setErrorsBiaValidation(errors);
                setAllBiaValidationState(BiaValidationState.Error);
            } else {
                setAllBiaValidationState(BiaValidationState.Success);
            }
            setSelected([]);
            setDisplaySelected(false);
            setBiaDiseabled(makeUnique(biaDiseabledList));
        });
    };

    /**
     * Creates the cache data object for a BIA post.
     * @param {Object} biaPost - The BIA post object.
     * @param {Boolean} sansSuite - Whether to validate the post without further action.
     * @param {string} mode - The mode used to validate the BIA (form or lot).
     * @returns {Object} The cache data object.
     */
    const createBiaCacheData = (biaToValidate, sansSuite, mode): CacheData => {
        const dateEmbauche = moment(biaToValidate.dateEmbauche).format('yyyy-MM-DD');
        let dateEffet = moment(biaToValidate.dateEffet).format('yyyy-MM-DD');

        return {
            action: enumStatusCommand.VALIDATE_BIA,
            idAssure: biaToValidate.idAssure,
            dateEmbauche: dateEmbauche,
            dateEffet: dateEffet,
            sansSuite: sansSuite, // TODO :: remove (initialement l'utilisateur n'a pas fait de choix)
            contIntern: biaToValidate.contIntern,
            newContrat: biaToValidate.contIntern,
            id: biaToValidate.id,
            nom: biaToValidate.nom,
            prenom: biaToValidate.prenom,
            numSecu: biaToValidate.numSecu,
            mode,
        };
    };

    /**
     * Delays the execution of a callback function until a specified transition time has elapsed since a given start time.
     * @param start - The start time.
     * @param transition - The transition time, in milliseconds.
     * @param callback - The callback function to execute after the transition time has elapsed.
     * @returns void
     */
    const delayCallback = (start: Date, transition: number, callback: () => void): void => {
        const elapsedTime: number = new Date().valueOf() - start.valueOf();
        if (elapsedTime >= transition) {
            callback();
        } else {
            setTimeout(callback, transition - elapsedTime);
        }
    };

    /**
     * Calculates the effective date based on the employee's hiring date and the selected contract.
     * Calculation happen when souscrip is changed or dateEffet is null
     *
     * @param {Date} dateEmbauche - The employee's hiring date.
     */
    const calculateDateEffet = (dateEmbauche) => {
        const datDeb = moment(selectedContract.debVal);

        if (dateEmbauche && moment(dateEmbauche).diff(datDeb, 'days') < 0) {
            dateEmbauche = new Date(selectedContract.debVal);
        } else {
            // convert moment date
            dateEmbauche = dateEmbauche ? new Date(dateEmbauche) : null;
        }

        // Calculate dateEffet on souscrip change or it is null
        if (optionSelected?.idSouscripteur || (isValid(dateEmbauche) && !getValues('dateEffet'))) {
            let dateEffet = null;

            // Calculate the effective date based on the selected contract type (effet)
            switch (selectedContract.codeTypeEffet) {
                // Case 1: Add X number of months to the hiring date, where X is the employee's seniority.
                case 1:
                    dateEffet = addMonths(dateEmbauche, selectedContract.anciennete);
                    break;
                // Case 2: Add 1 month to the hiring date and set the day to the 1st of the next month.
                case 2:
                    const nextMonth = addMonths(dateEmbauche, 1);
                    dateEffet = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
                    break;
                // Case 3: Add 2 months to the hiring date, unless the hiring date is the 1st of the month, in which case only 1 month is added.
                case 3:
                    const isFirstDayOfMonth = moment(dateEmbauche).date() === 1;
                    const monthsToAdd = isFirstDayOfMonth ? 1 : 2;
                    dateEffet = addMonths(dateEmbauche, monthsToAdd);
                    break;
                default:
                    throw new Error('Missing code type effet');
            }

            const dateEffetMoment = moment(dateEffet);

            // If the effective date is before the contract start date, use the contract start date instead.
            if (dateEffetMoment.diff(datDeb, 'days') < 0) {
                dateEffet = new Date(selectedContract.debVal);
            }

            if (dateEffet && isValid(dateEffet)) {
                setSelectedDateEffet(dateEffet);
                setValue('dateEffet', format(dateEffet, 'dd/MM/yyyy'));
            }
        }
    };

    const handleDateEmbaucheChange = (dateEmbauche, hasChanged = true) => {
        const isValidDate = moment(dateEmbauche, 'YYYY-MM-DD', true).isValid();
        if (!isValidDate) dateEmbauche = null;
        if (!isBiaValidated) {
            setValue('dateEmbauche', dateEmbauche);
            setSelectedDateEmbauche(dateEmbauche);
            trigger();
        }
        calculateDateEffet(dateEmbauche);
        if (hasChanged) {
            // TODO :: remove hasBiaChanged
            setHasDateEmbaucheChanged(true);
        }
    };

    const handleDateEffetChange = (dateEffet) => {
        const momentDateEffet = moment(dateEffet, 'YYYY-MM-DD').format('DD/MM/YYYY');
        setValue('dateEffet', momentDateEffet);
        setSelectedDateEffet(dateEffet);
        setHasDateEmbaucheChanged(true);
        trigger('dateEffet');
    };

    const handleExport = () => {
        setDownloading(true);
        exportBias().then((data) => {
            setDownloading(false);
            const url = window.URL.createObjectURL(
                new Blob([data as BlobPart], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bias.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    };

    useEffect(() => {
        const currentBIA = onGoingCommand ? onGoingCommand.data : selectedContract;
        if (currentBIA.dateEmbauche) {
            // handleDateEmbaucheChange(moment(currentBIA.dateEmbauche), false);
        } else {
            handleDateEmbaucheChange(null, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContract, onGoingCommand]);

    /**
     * Get list of BIA command waiting for treatment
     * @param {Array} keyList - List of keys to retrieve BIA commands.
     * @returns {Array} - List of disabled BIA command IDs.
     */
    const getOngoingCommandsFromServer = async (keyList) => {
        let biaDiseabledList = [];
        if (keyList.length) {
            // Get ongoing commands with status "ValidateBiaCommand"
            await statusCommand.getCommand(keyList, [enumStatusCommand.VALIDATE_BIA]).then((response2: any) => {
                const commandList = [];
                if (response2[enumStatusCommand.VALIDATE_BIA]) {
                    // For each ongoing command, add it to the commandList and create a mercure listener for it
                    response2[enumStatusCommand.VALIDATE_BIA].forEach((c) => {
                        const command = JSON.parse(c.data);
                        commandList.push({ id: c.id, data: command });
                        createMercureListener(c.id);
                        biaDiseabledList = [...biaDiseabledList, command.id];
                    });
                }
                setOngoingValidates(commandList);
            });
        }
        return biaDiseabledList;
    };

    /**
     * Get list of BIA and BIA in treatment
     * @returns void
     */
    const loadBia = () => {
        setLoading(true);
        setBiaInError([]);
        let biaDiseabledList = [];
        bia.getBias()
            .then(async (response: any) => {
                setContrats(response);
                // Get unique key pairs of idSouscripteur and tarifCoti
                const keyList = Array.from(new Set(response.map((o) => `${o.idSouscripteur}-${o.tarifCoti}`)));
                biaDiseabledList = await getOngoingCommandsFromServer(keyList);
            })
            .catch(() => toast.error(t('affiliations.contrat.errorGet')))
            .finally(() => {
                // Diseable BIA to avoid new validation
                setBiaDiseabled(makeUnique(biaDiseabledList));
                setLoading(false);
            });

        return () => {
            eventListRef.current.forEach((o) => o.close());
        };
    };

    useEffect(() => {
        loadBia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    type FormData = {
        optionSelected: string;
        dateEmbauche: string;
        dateEffet: string;
    };

    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;

    // Validate form fields
    const validationSchema: Yup.SchemaOf<FormData> = Yup.object().shape({
        optionSelected: Yup.mixed().test('optionSelected', 'Champ requis', function (value) {
            if (isLoadingOptions || value?.value) return true;
            return yup.date().isValidSync(value);
        }),
        dateEmbauche: Yup.mixed()
            .required()
            .test('dateEmbauche', "Date d'embauche invalide", function (value) {
                if (isLoadingOptions || value?.value) return true;
                return yup.date().isValidSync(value);
            }),
        dateEffet: Yup.string()
            .required()
            .test('dateEffet', "Date d'effet invalide", function (value) {
                if (!value) return false;
                if (!dateRegex.test(value)) return false;
                const [day, month, year] = value.split('/');
                const dateObj = new Date(`${year}-${month}-${day}`);
                return !isNaN(dateObj.getTime()) && value !== '01/01/1900';
            })
            .test('DebutContratEffet', `La date d'effet ne peut pas être antérieure au contrat (${moment(optionSelected?.datDeb, 'YYYY-MM-DD').format('DD/MM/YYYY')})`, function (value) {
                if (isLoadingOptions) return true;
                const debutContratEmbauche = moment(optionSelected?.datDeb, 'YYYY-MM-DD');
                const dateEffet = moment(value, 'DD-MM-YYYY').format('YYYY-MM-DD');
                return debutContratEmbauche.isSameOrBefore(dateEffet);
            })
            .test('DebutEmbaucheEffet', `La date d'effet ne peut pas être antérieure à la date d'embauche`, function (value, context) {
                if (isLoadingOptions || !context.parent.dateEmbauche) return true;
                const dateEmbauche = moment(context.parent.dateEmbauche, 'YYYY-MM-DD').startOf('day');
                const dateEffet = moment(value, 'DD-MM-YYYY').startOf('day').format('YYYY-MM-DD');
                return dateEmbauche.isSameOrBefore(dateEffet);
            })
            .matches(/^\d{2}\/\d{2}\/\d{4}$/),
    });

    //* UseForm initialisation
    const methods = useForm<FormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: {},
    });

    const {
        trigger,
        setValue,
        getValues,
        watch,
        formState: { errors },
    } = methods;

    const selectedDateEmbauche2 = watch('dateEmbauche');

    const calculateMinDateEffet = (selectedDateEmbauche) => {
        if (!selectedContract || !selectedDateEmbauche) return;
        // selectedContract
        const dateEmbauche = new Date(selectedDateEmbauche);
        let minDateEffet = null;

        // Calculate the effective date based on the selected contract type (effet)
        switch (selectedContract.codeTypeEffet) {
            // Case 1: Add X number of months to the hiring date, where X is the employee's seniority.
            case 1:
                minDateEffet = addMonths(dateEmbauche, selectedContract.anciennete);
                break;
            // Case 2: Add 1 month to the hiring date and set the day to the 1st of the next month.
            case 2:
                const nextMonth = addMonths(dateEmbauche, 1);
                minDateEffet = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
                break;
            // Case 3: Add 2 months to the hiring date, unless the hiring date is the 1st of the month, in which case only 1 month is added.
            case 3:
                const isFirstDayOfMonth = moment(dateEmbauche).date() === 1;
                const monthsToAdd = isFirstDayOfMonth ? 1 : 2;
                minDateEffet = addMonths(dateEmbauche, monthsToAdd);
                break;
            default:
                throw new Error('Missing code type effet');
        }

        // Get year, month, and day
        const year = minDateEffet.getFullYear();
        const month = String(minDateEffet.getMonth() + 1).padStart(2, '0'); // Adding 1 because getMonth() returns zero-based month index
        const day = String(minDateEffet.getDate()).padStart(2, '0');

        // Construct the formatted date string
        const formattedDate = `${year}/${month}/${day}`;
        setMinDateEffet(formattedDate);
    };

    useEffect(() => {
        calculateMinDateEffet(selectedDateEmbauche2);
    }, [selectedDateEmbauche2]);

    const handleOptionSelected = (option) => {
        if (!option) return;
        setOptionSelected(option);
        setValue('optionSelected', option);
        setHasBiaChanged(true); // TODO :: remove hasBiaChange when getting full list of contract with date
        const newContrat = { ...selectedContract };
        newContrat.debVal = option.datDeb;
        newContrat.dateEmbauche = selectedDateEmbauche;
        setSelectedContract(newContrat);
        trigger();
    };

    const makeUnique = (ar) => {
        let j = {};

        ar.forEach(function (v) {
            j[v + '::' + typeof v] = v;
        });

        return Object.keys(j).map(function (v) {
            return j[v];
        });
    };

    const handleSelectAllClick = async () => {
        const ids = contrats.map((e: any) => e.id);
        // Validate data
        let resetSelection = false;
        await Promise.all(
            ids.map(async (id) => {
                const isValid = await handleSelectedValidation(id);
                if (!isValid) resetSelection = true;
            }),
        );
        if (!resetSelection) {
            const idsSelectable = ids.filter((id) => !biaDiseabled.includes(id));
            setSelected(makeUnique(idsSelectable));
        }
    };

    const handleShowSelected = () => setDisplaySelected(!displaySelected);
    const isAllSelected = useCallback((): boolean => {
        const ids = contrats.map((e) => e.id);
        const hasAll = ids.every((value: any) => selected.includes(value));
        return hasAll;
    }, [contrats, selected]);

    function ToolsBox() {
        return (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }} className="mt-2 mb-n2">
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start' }} className={'pl-5 mb-n5'}>
                    <Box display={'flex'}>
                        <SGSTooltip title="Tout sélectionner">
                            <SGSButton
                                color="light"
                                className={classNames(styles.selectedBia, isAllSelected() ? styles.selected : '', 'mt-3 pl-2 ')}
                                icon="fa fa-check fa-lg ml-1 mr-n2 ml-n1"
                                onClick={() => handleSelectAllClick()}
                                disabled={loading || isVisitor(user)}
                            ></SGSButton>
                        </SGSTooltip>
                    </Box>
                    <Box display={'flex'}>
                        <SGSTooltip title="Tout désélectionner">
                            <SGSButton
                                color="light"
                                className={classNames(styles.selectedBia, 'mt-3 ml-2')}
                                icon="fa fa-times fa-lg mr-n1 ml-n1"
                                onClick={() => setSelected([])}
                                disabled={loading || isVisitor(user)}
                            ></SGSButton>
                        </SGSTooltip>
                    </Box>
                    <Box display={'flex'}>
                        <SGSTooltip title="Afficher les éléments sélectionnés">
                            <SGSButton
                                color="light"
                                className={classNames(styles.selectedBia, displaySelected ? styles.selected : '', 'mt-3 ml-2')}
                                icon="fa fa-eye fa-lg mr-n1 ml-n1"
                                onClick={() => handleShowSelected()}
                                disabled={loading || isVisitor(user)}
                            ></SGSButton>
                        </SGSTooltip>
                    </Box>
                    <Box display={'flex'}>
                        <SGSTooltip title="Recharger les données">
                            <SGSButton
                                color="light"
                                className={classNames(styles.selectedBia, 'mt-3 ml-2')}
                                icon={loading ? 'fas fa-sync-alt fa-spin fa-lg mr-n1 ml-n1' : 'fas fa-sync-alt fa-lg mr-n1 ml-n1'}
                                onClick={() => loadBia()}
                                disabled={loading || isVisitor(user)}
                            ></SGSButton>
                        </SGSTooltip>
                    </Box>
                    <Box display={'flex'}>
                        <SGSButton disabled={!selected.length || isVisitor(user)} className={classNames(styles.selectedBia, 'mt-3 ml-2')} icon="fa fa-check fa-lg" onClick={handleOpen}>
                            Valider le{selected.length > 1 ? 's' : ''} <strong>{selected.length}</strong> BIA sélectionné{selected.length > 1 ? 's' : ' '}
                        </SGSButton>
                    </Box>
                </Box>
            </Box>
        );
    }

    const handleOpen = () => {
        setAllBiaValidationState(BiaValidationState.Initial);
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    function BootstrapDialogTitle(props) {
        const { children, onClose, ...other } = props;

        return (
            <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
                {children}
                {onClose ? (
                    <>
                        {' '}
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            style={{
                                position: 'absolute',
                                right: 15,
                                top: 20,
                            }}
                        >
                            <GridCloseIcon
                                style={{
                                    position: 'absolute',
                                    right: 15,
                                    top: 15,
                                    color: '#FFF',
                                }}
                            />
                        </IconButton>
                    </>
                ) : null}
            </DialogTitle>
        );
    }

    /**
     * Displays a message based on the current state of the BIA validation process.
     * @param {Array} errorsBiaValidation - List of errors that occurred during validation.
     * @returns {JSX.Element} The message to display as a React element.
     */
    const DisplayMsg = (errorsBiaValidation) => {
        let displayMessage = null;

        // Success and error messages based on the number of selected items
        const successMessage = selected.length > 1 ? `Vos ${selected.length} demandes ont été prises en compte !` : 'Votre demande a été prise en compte !';
        const errorMessage = errorsBiaValidation.length > 1 ? `${errorsBiaValidation.length} erreurs sont survenues durant le traitement ! ` : 'Une erreur est survenue durant le traitement !';

        // Switch statement to determine what message to display based on the current state
        switch (allBiaValidationState) {
            case BiaValidationState.Initial:
                // Display a confirmation message before processing begins
                displayMessage = (
                    <Box display="flex" flexDirection="column" className="mt-3">
                        <Typography className={customStyles.txtBlueLightBold} style={{ marginBottom: '8px' }}>
                            Vous êtes sur le point de valider {selected.length} BIA sur un total de {contrats.length}
                        </Typography>
                        <Typography className={customStyles.txtBlueLight}>
                            Souhaitez-vous continuer ?{' '}
                            <Link to={'#'} onClick={handleClose}>
                                Annuler
                            </Link>
                        </Typography>
                    </Box>
                );
                break;
            case BiaValidationState.Processing:
                // Display a loading spinner while processing is underway
                displayMessage = (
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={40} style={{ marginRight: '10px' }} />
                        <Typography style={{ marginBottom: '0' }} className={customStyles.txtBlueLightBold}>
                            En cours de traitement...
                        </Typography>
                    </Box>
                );
                break;
            case BiaValidationState.Success:
                // Display a success message when processing is complete
                displayMessage = (
                    <>
                        <Box className={classNames(customStyles.txtBlueLightBold)}>
                            <Box display={'flex'}>
                                <Box className={customStyles.icon}>
                                    <i className="fas fa-check-circle"></i>
                                </Box>
                                <Box className={classNames(customStyles.iconText, customStyles.txtBlueLightBold, 'ml-2')}> {successMessage}</Box>
                            </Box>
                        </Box>
                    </>
                );
                break;
            case BiaValidationState.Error:
                // Display an error message if processing encountered any errors
                displayMessage = (
                    <Alert severity="error" style={{ width: '100%' }}>
                        <AlertTitle>Erreur</AlertTitle>
                        <Typography className={customStyles.txtBlueLightBold} style={{ marginBottom: '8px' }}>
                            {errorMessage}
                        </Typography>
                    </Alert>
                );
                break;
            default:
                // Throw an error if an unknown state is encountered
                throw new Error('No state found');
        }

        // Return the message inside a box with some styling
        return <Box style={{ display: 'flex', alignItems: 'center', minHeight: '50px' }}>{displayMessage}</Box>;
    };

    /**
     * Component that displays a message when the list of selected BIA items is empty.
     * @returns {JSX.Element} The JSX element that displays the message.
     */
    const MsgListEmpty = () => {
        if (displaySelected)
            return (
                <Box>
                    Vous n'avez sélectionné aucun BIA <br />
                    <br />
                    <Link to="#" onClick={() => setDisplaySelected(false)}>
                        Afficher toute la liste
                    </Link>
                </Box>
            );
        return t('affiliations.contrat.bia.noResult');
    };

    const biaStatut = (id) => {
        if (biaValidated.includes(id)) return BiaStatut.VALIDATED;
        if (!isSubmitDiseabled && biaInError.includes(id)) return BiaStatut.ERROR;
        if (!!onGoingCommand || biaDiseabled.includes(id) || isSubmitDiseabled) return BiaStatut.ON_GOING;
        return BiaStatut.TO_BE_VALIDATED;
    };

    const isBiaDisabled = (id) => {
        return !!onGoingCommand || isBiaValidated || biaValidated.includes(id) || isLoadingOptions;
    };

    const schema: yup.SchemaOf<BIA> = yup.object().shape({
        id: yup.number().required(),
        idAssure: yup.string().required(),
        tarifCoti: yup.string().required(),
        idSouscripteur: yup.string().required(),
        numSecu: yup.string().required(),
        nom: yup.string().required(),
        prenom: yup.string().required(),
        dateNaissance: yup.string().required(),
        dateEffet: yup
            .string()
            .nullable(false) // Value cannot be null
            .required("La date d'effet est obligatoire.")
            .test('valid-date-effet', "La date d'effet n'est pas valide.", function (value) {
                return yup.date().isValidSync(value) && value !== '1970-01-01';
            })
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Le format de date d\'effet doit être "AAAA-MM-JJ" (ex : 2023-06-08).'),
        dateEmbauche: yup
            .string()
            .required("La date d'embauche est obligatoire")
            .nullable(false)
            .test('valid-date-embauche', "La date d'embauche n'est pas valide.", function (value) {
                return yup.date().isValidSync(value) && value !== '1970-01-01';
            })
            .matches(/^\d{4}-\d{2}-\d{2}$/, 'Le format de date d\'embauche doit être "AAAA-MM-JJ" (ex : 2023-06-08).'),
        siret: yup.string().required(),
        libelle: yup.string().required(),
        contIntern: yup.string().required(),
        info: yup.string(), //.required(),
        produit: yup.string().required(),
        idBiaContrat: yup.number().required(),
        createdAt: yup.string().required(),
        modifiedAt: yup.string().required(),
        contrat: yup.string().required(),
        anciennete: yup.number().required(),
        codeTypeEffet: yup.number().required(),
        libelleTypeEffet: yup.string().required(),
        debVal: yup.string().required(),
    });

    /**
     *
     * @param data Validate selected BIA fields
     * @returns
     */
    const validateFields = (data: BIA) => {
        const fieldNames = {
            id: 'Identifiant',
            idAssure: "Identifiant de l'assuré",
            tarifCoti: 'Tarif de cotisation',
            idSouscripteur: 'Identifiant du souscripteur',
            numSecu: 'Numéro de sécurité sociale',
            nom: 'Nom',
            prenom: 'Prénom',
            dateNaissance: 'Date de naissance',
            dateEmbauche: "Date d'embauche",
            dateEffet: "Date d'effet",
            siret: 'Numéro SIRET',
            libelle: 'Libellé',
            contIntern: 'Contrat interne',
            info: 'Option',
            produit: 'Produit',
            idBiaContrat: 'Identifiant du BIA de contrat',
            createdAt: 'Date de création',
            modifiedAt: 'Date de modification',
            contrat: 'Contrat',
            anciennete: 'Ancienneté',
            codeTypeEffet: "Code du type d'effet",
            libelleTypeEffet: "Libellé du type d'effet",
            debVal: 'Date de début de validité',
        };
        return schema
            .validate(data, { abortEarly: false })
            .then(() => {
                return false;
            })
            .catch((errors) => {
                const fieldErrors = {};
                errors.inner.forEach((error) => {
                    const fieldName = fieldNames[error.path] || error.path;
                    fieldErrors[fieldName] = error.message;
                });

                return fieldErrors;
            });
    };

    const displayModalSelectionError = ({ selectedBia, errors }: { selectedBia: BIA; errors: any }) => {
        const error = {
            id: selectedBia.id,
            info: selectedBia.info,
            nom: selectedBia.nom,
            prenom: selectedBia.prenom,
            numSecu: selectedBia.numSecu,
            contrat: selectedBia.tarifCoti,
            souscrip: selectedBia.libelle,
            errors,
        };

        setErrorForBiaSelection(error);
        setIsModalOpen(true);
    };

    const ErrorList = ({ errors }) => {
        return (
            <Box>
                {Object.keys(errors).map((key) => (
                    <Box key={key} display="flex" alignItems="center">
                        <Typography className={modalLightStyles.error_text}>{key} : </Typography>
                        <Typography className={classNames(modalLightStyles.error_text, 'ml-1')}> {errors[key]}</Typography>
                    </Box>
                ))}
            </Box>
        );
    };
    /**
     * Find selected BIA and display an error message if there are invalid fields
     * @param id - BIA ID
     * @returns boolean (true is valid, false invalide)
     */
    const handleSelectedValidation = async (id) => {
        // Find the selected BIA based on the ID
        const selectedBia: BIA = contrats.find((c) => c.id === id);
        // Handle case when selected BIA is not found
        if (!selectedBia) return false;
        // Check if all fields are filled and valid
        const errors = await validateFields(selectedBia);
        const isValid = !errors;

        if (errors) {
            displayModalSelectionError({ selectedBia, errors });
        }

        return isValid;
    };

    const fields = [
        { label: 'Nom', value: `${errorForBiaSelection?.nom} ${errorForBiaSelection?.prenom}` },
        { label: 'Contrat', value: errorForBiaSelection?.contrat },
        { label: 'Société', value: errorForBiaSelection?.souscrip },
        { label: 'Option', value: errorForBiaSelection?.info },
    ];

    const closeModal = () => {
        setIsModalOpen(false);
        delay(setErrorForBiaSelection, 200, null);
    };

    const openFormValidation = (id) => {
        const data = contrats.find((row) => row.id === id);
        const row: any = { original: { ...data } };
        closeModal();
        onRowClick(row);
    };

    const isDateEmbaucheDisabled = (idContract) => {
        if (biaStatut(idContract) === BiaStatut.ON_GOING) return true;
        if (biaStatut(idContract) === BiaStatut.VALIDATED) return true;
        return false;
    };

    const ModalSelectionError = () => {
        const actionToolbar = () => {
            return (
                <Box className="text-right mt-4 mb-4" display="flex" justifyContent="flex-end">
                    <Box className="mr-2">
                        <Button variant="contained" color="primary" onClick={handleClose} className={commonStyles.btnBlue} onClickCapture={() => openFormValidation(errorForBiaSelection.id)}>
                            Valider à l'aide du formulaire
                        </Button>
                    </Box>
                    <Button variant="contained" color="primary" className={commonStyles.btnRed} onClick={closeModal}>
                        Fermer
                    </Button>
                </Box>
            );
        };
        return (
            <>
                <SGSModalLight
                    isOpenModal={isModalOpen}
                    setIsOpenModal={setIsModalOpen}
                    title={'Anomalie détectée : Impossible de sélectionner ce BIA'}
                    titleIcon={errorIcon}
                    actionToolbar={actionToolbar}
                >
                    <Box className={modalLightStyles.modalContent__title}>Vous ne pouvez pas sélectionner ce BIA :</Box>
                    {errorForBiaSelection && (
                        <>
                            <List dense className="mb-2">
                                {fields.map((field, index) => (
                                    <ListItem key={index} style={{ margin: 0, padding: 0 }}>
                                        <ListItemText style={{ margin: 0, padding: 0 }}>
                                            <Box style={{ display: 'flex' }}>
                                                <Typography component="div" className={commonStyles.txtBlueLightBold} style={{ minWidth: 75 }}>
                                                    {field.label} :
                                                </Typography>
                                                <Typography component="div" className={commonStyles.txtBlueDarkBold}>
                                                    {field.value}
                                                </Typography>
                                            </Box>
                                        </ListItemText>
                                    </ListItem>
                                ))}
                            </List>
                            <Box className={modalLightStyles.light}>Le(s) erreur(s) suivante(s) ont été détectée(s):</Box>
                            <Box className="mt-3 mb-3">
                                <ErrorList errors={errorForBiaSelection.errors} />
                            </Box>
                            <Box className={modalLightStyles.darkBold}>Veuillez valider ce BIA en utilisant le formulaire</Box>
                        </>
                    )}
                </SGSModalLight>
            </>
        );
    };

    const canModifyDateEffet = () => {
        const numInterneDateEffet = process.env.REACT_APP_NUM_INTERNE_DATE_EFFET || '';
        return numInterneDateEffet.includes(user.num_adherent);
    };

    const minDateModifiable = () => {
        const minDate = moment(minDateEffet, 'YYYY/MM/DD');
        const today = moment();
        const threeMonthsAgo = today.subtract(3, 'months');
        console.log(minDate, threeMonthsAgo);
        return minDate <= threeMonthsAgo ? threeMonthsAgo.format('YYYY/MM/DD') : minDateEffet;
        //return threeMonthsAgo.format('YYYY/MM/DD');
    };

    const maxDateModifiable = () => {
        const today = moment();
        const threeMonthsInFuture = today.add(3, 'months');
        return threeMonthsInFuture.format('YYYY/MM/DD');
    };

    return (
        <>
            <ModalSelectionError />
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="responsive-dialog-title"
                PaperProps={{
                    style: { width: '100%', maxHeight: '80vh', borderRadius: 10 },
                }}
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose} className={styles.header}>
                    <h2 className={styles.header__text}>Validation de BIA</h2>
                </BootstrapDialogTitle>
                <DialogContent>
                    <DialogContentText className="m-2" style={{ minHeight: '90px', width: '500px' }}>
                        {DisplayMsg(errorsBiaValidation)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="mr-2 mb-2" style={{ minHeight: '59px' }}>
                    {allBiaValidationState === BiaValidationState.Initial && (
                        <>
                            <SGSTextButton color="blue" variant="outlined" icon="far fa-check-circle" size="large" onClick={() => validateAllBiaPosts([...selected], false)}>
                                {t('affiliations.contrat.bia.valider')}
                            </SGSTextButton>
                            <SGSTextButton color="orange" variant="outlined" icon="far fa-times-circle" size="large" onClick={() => validateAllBiaPosts([...selected], true)}>
                                {t('affiliations.contrat.bia.withoutContinuation')}
                            </SGSTextButton>
                        </>
                    )}
                    {allBiaValidationState === BiaValidationState.Success && (
                        <>
                            <SGSTextButton color="blue" variant="outlined" icon="far fa-check-circle" size="large" onClick={() => handleClose()}>
                                Fermer
                            </SGSTextButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <SGSHelpButton />
            <ToolsBox />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}>
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start' }}></Box>
                <SGSButton color="primary" onClick={() => handleExport()} icon="far fa-file-excel fa-lg" className={styles.export} loading={downloading}>
                    {t('global.export')}
                </SGSButton>
            </Box>

            <Box>
                <SGSCard className="w-100">
                    <CardContent className="d-flex flex-column justify-content-center">
                        <Box>
                            <SGSTable
                                selectable={true}
                                columns={columns}
                                data={displaySelected ? contrats.filter((c: any) => selected.includes(c.id)) : contrats}
                                onRowClick={onRowClick}
                                disableFooter
                                loading={loading}
                                noRowsLabel={MsgListEmpty()}
                                selected={selected}
                                setSelected={setSelected}
                                diseabled={biaDiseabled}
                                handleSelectedValidation={handleSelectedValidation}
                            ></SGSTable>
                        </Box>
                    </CardContent>
                </SGSCard>
                <SGSDrawer open={menuOpen} toggleDrawer={toggleDrawer} containerStyle="container-W-800">
                    {!isLoadingOptions ? (
                        <>
                            <Box className={styles.summary}>
                                <Box>
                                    <Box className={styles.selectedPerson}>
                                        <Box>
                                            {t('affiliations.contrat.bia.numMin')} {selectedContract.numSecu}
                                        </Box>
                                        <Typography variant="h3">
                                            {selectedContract.nom} {selectedContract.prenom}
                                        </Typography>
                                    </Box>

                                    <Box className="mt-2">
                                        <Box>
                                            {t('affiliations.contrat.bia.numSiretMin')} {selectedContract.siret}
                                        </Box>
                                        <Box className={styles.selectedLibelle}>{selectedContract.libelle}</Box>

                                        <Box>
                                            <Box display="flex" className="mt-3">
                                                <Typography style={{ minWidth: '130px' }}>Type effet : </Typography>
                                                <Typography className="ml-1">{selectedContract.libelleTypeEffet}</Typography>
                                            </Box>
                                            <Box display="flex">
                                                <Typography style={{ minWidth: '130px' }}>Ancienneté : </Typography>
                                                <Typography className="ml-1">{selectedContract.anciennete} mois</Typography>
                                            </Box>
                                        </Box>
                                        <Box display={'flex'}>
                                            <Box className="mt-3 w-50">
                                                <Box>{t('affiliations.contrat.bia.dateEmbauche')} : </Box>
                                                <Box>
                                                    <FormProvider {...methods}>
                                                        <SGSDatePicker
                                                            //minDate={selectedContract.debVal ? new Date(selectedContract.debVal) : undefined}
                                                            name="dateEmbauche"
                                                            variant="inline"
                                                            format="dd/MM/yyyy"
                                                            disabled={isDateEmbaucheDisabled(selectedContract.id)}
                                                            value={selectedDateEmbauche}
                                                            onChange={handleDateEmbaucheChange}
                                                        ></SGSDatePicker>
                                                    </FormProvider>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Box className="mt-3 ml-5">Date d'effet :</Box>
                                                <Box className="ml-5 mt-1" style={{ color: '#969797', fontWeight: 'Bold', fontSize: '16px' }}>
                                                    {canModifyDateEffet() ? (
                                                        <FormProvider {...methods}>
                                                            <SGSDatePicker
                                                                minDate={minDateModifiable()}
                                                                maxDate={maxDateModifiable()}
                                                                name="dateEffet"
                                                                variant="inline"
                                                                format="dd/MM/yyyy"
                                                                disabled={isDateEmbaucheDisabled(selectedContract.id) || !selectedDateEmbauche}
                                                                value={selectedDateEffet}
                                                                onChange={handleDateEffetChange}
                                                            >
                                                                <Button>Annuler</Button>
                                                            </SGSDatePicker>
                                                        </FormProvider>
                                                    ) : (
                                                        getValues('dateEffet')
                                                    )}
                                                </Box>
                                                {errors?.dateEffet && getValues('dateEmbauche') ? (
                                                    <Box className="ml-5 mt-1 ">
                                                        <strong className={styles.error}>Date d'effet invalide</strong>
                                                    </Box>
                                                ) : (
                                                    ''
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box className="mt-5">
                                        {/* <Box>Société</Box> */}
                                        <Box>
                                            <FormProvider {...methods}>
                                                <SGSFormAutoComplete
                                                    variant="outlined"
                                                    name="optionSelected"
                                                    label="affiliations.contrat.bia.company"
                                                    options={listContrat ?? []}
                                                    loading={loading2}
                                                    handleChange={handleOptionSelected}
                                                    value={optionSelected || null}
                                                    disabled={isBiaDisabled(selectedContract.id)}
                                                />
                                            </FormProvider>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="mb-5" style={{ minHeight: '64px' }}>
                                {isSubmitDiseabled ? (
                                    <Box className={classNames(customStyles.txtBlueLightBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                        <Box className={customStyles.icon}>
                                            <i className="fas fa-stopwatch"></i>
                                        </Box>
                                        <Box className={classNames(customStyles.iconText, customStyles.txtBlueLightBold)}>Demande en cours d'envoi</Box>
                                    </Box>
                                ) : (
                                    ''
                                )}

                                {biaStatut(selectedContract.id) === BiaStatut.ON_GOING && !isSubmitDiseabled ? (
                                    <Box>
                                        <Box className={classNames(customStyles.txtGrayLightBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                            <Box className={customStyles.icon}>
                                                <i className="fas fa-hourglass"></i>
                                            </Box>
                                            <Box className={classNames(customStyles.iconText, customStyles.txtGrayLightBold)}>Une demande de traitement est en cours</Box>
                                        </Box>
                                        {errors?.dateEffet && getValues('dateEmbauche') ? (
                                            <Box className="ml-5 mt-1 ">
                                                <strong className={styles.error}>Date d'effet invalide</strong>
                                            </Box>
                                        ) : (
                                            ''
                                        )}
                                    </Box>
                                ) : (
                                    ''
                                )}

                                {biaStatut(selectedContract.id) === BiaStatut.VALIDATED ? (
                                    <>
                                        <Box>
                                            <Box className={classNames(customStyles.txtGreenDarkBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                                <Box className={customStyles.icon}>
                                                    <i className="fas fa-check-circle"></i>
                                                </Box>
                                                <Box className={classNames(customStyles.iconText, customStyles.txtGreenDarkBold)}>Votre demande a été prise en compte</Box>
                                            </Box>
                                        </Box>
                                    </>
                                ) : (
                                    ''
                                )}

                                {biaStatut(selectedContract.id) === BiaStatut.ERROR ? (
                                    <>
                                        <Box>
                                            <Box className={classNames(customStyles.txtRedDarkBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                                <Box className={customStyles.icon}>
                                                    <i className="fas fa-exclamation-circle"></i>
                                                </Box>
                                                <Box className={classNames(customStyles.iconText, customStyles.txtRedDarkBold)}>Une erreur est survenue !</Box>
                                            </Box>
                                        </Box>
                                        <Box className={classNames(customStyles.txtBlueLight, 'text-center')}>Veuillez réessayer ultérieurement ou prendre contact avec votre gestionnaire</Box>
                                    </>
                                ) : (
                                    ''
                                )}

                                {!getValues('dateEmbauche') && !isDateEmbaucheDisabled(selectedContract.id) ? (
                                    <Box className="text-center  ">
                                        <strong className={styles.error}>Veuillez sélectionner une date d'embauche pour calculer la date d'effet</strong>
                                    </Box>
                                ) : (
                                    ''
                                )}
                            </Box>

                            <Box className={styles.actions}>
                                <h4>{t('affiliations.contrat.bia.actionsAvailable')} :</h4>
                                <Box>
                                    <SGSTextButton
                                        color="blue"
                                        variant="outlined"
                                        icon="far fa-check-circle"
                                        size="large"
                                        disabled={isBiaDisabled(selectedContract.id) || isSubmitDiseabled || Object.keys(errors).length > 0 || isVisitor(user)}
                                        onClick={() => handleValidate(selectedContract.idAssure, false)}
                                    >
                                        {t('affiliations.contrat.bia.valider')}
                                    </SGSTextButton>
                                    <SGSTextButton
                                        className="ml-3"
                                        color="orange"
                                        variant="outlined"
                                        icon="far fa-times-circle"
                                        size="large"
                                        disabled={isBiaDisabled(selectedContract.id) || isSubmitDiseabled || Object.keys(errors).length > 0 || isVisitor(user)}
                                        onClick={() => handleValidate(selectedContract.idAssure, true)}
                                    >
                                        {t('affiliations.contrat.bia.withoutContinuation')}
                                    </SGSTextButton>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh', // Adjust this value based on your needs
                            }}
                        >
                            <Box className={styles.loading}>
                                <Loader loading={true} />
                            </Box>
                        </Box>
                    )}
                </SGSDrawer>
            </Box>
        </>
    );
};

export default BiaEnLigne;
